import { capitalize } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'

import { tokPx } from '@sunwebgroup/ui-components'
import { Dropdown, focusOutline } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'
import { calculateDayRange, calculateMonthRange, calculateYearRange } from './dateHelpers'

interface DateDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  value: Date
  min: Date
  max?: Date
  onValueChange?: (newDate: Date) => void
}

const DropdownGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
  width: 100%;
  justify-content: space-between;
  ${focusOutline()}

  & > :not(:first-child):not(:last-child) {
    border-radius: 0;
    flex: 1;
    border-left: none;
    border-right: none;
  }
  & > :first-child {
    border-radius: ${tokPx('primitives_corner_radius_l')} 0 0 ${tokPx('primitives_corner_radius_l')};
  }
  & > :last-child {
    border-radius: 0 ${tokPx('primitives_corner_radius_l')} ${tokPx('primitives_corner_radius_l')} 0;
  }
`

const DropdownTrigger = styled(Dropdown.Trigger)`
  &:focus-within {
    z-index: 1;
  }
`
function DateDropdown({ value, min, max = new Date(), onValueChange, className }: DateDropdownProps) {
  const intl = useIntl()

  const yearRange = React.useMemo(() => calculateYearRange(min, max), [min, max])
  const monthRange = React.useMemo(() => calculateMonthRange(value, min, max), [value, min, max])
  const dayRange = React.useMemo(() => calculateDayRange(value, min, max), [value, min, max])

  const handleDayChange = (e: string) => {
    const newDate = new Date(value.getFullYear(), value.getMonth(), Number.parseInt(e, 10))
    onValueChange?.(newDate)
  }

  const handleMonthChange = (e: string) => {
    const newDate = new Date(value.getFullYear(), Number.parseInt(e, 10) - 1, value.getDate())

    if (newDate < min || newDate > max) {
      const allowedRange = calculateDayRange(newDate, min, max)

      newDate.setDate(allowedRange[0])
    }

    onValueChange?.(newDate)
  }

  const handleYearChange = (e: string) => {
    const newDate = new Date(Number.parseInt(e, 10), value.getMonth(), value.getDate())

    const newDateIsOutOfRange = newDate < min || newDate > max
    const newDateMonthIsOutOfRange = newDate.getMonth() < min.getMonth() || newDate.getMonth() > max.getMonth()

    if (newDateIsOutOfRange && newDateMonthIsOutOfRange) {
      const allowedRange = calculateMonthRange(newDate, min, max)
      const allowedMonth = allowedRange[allowedRange.length - 1] - 1

      newDate.setMonth(allowedMonth)
    }

    if (newDate < min || newDate > max) {
      const allowedRange = calculateDayRange(newDate, min, max)

      newDate.setDate(allowedRange[0])
    }

    onValueChange?.(newDate)
  }

  const getMonthName = (month: number) => {
    const date = new Date(2000, month - 1, 1)
    return intl.formatDate(date, { month: 'long' })
  }

  return (
    <DropdownGroup className={className}>
      <Dropdown.Root onValueChange={e => handleDayChange(e)} value={String(value.getDate())}>
        <DropdownTrigger />
        <Dropdown.Content>
          {dayRange.map(day => (
            <Dropdown.Item value={day.toString()} key={day}>{day}</Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Root>
      <Dropdown.Root onValueChange={e => handleMonthChange(e)} value={String(value.getMonth() + 1)}>
        <DropdownTrigger />
        <Dropdown.Content>
          {monthRange.map(month => (
            <Dropdown.Item value={month.toString()} key={month}>{capitalize(getMonthName(month))}</Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Root>
      <Dropdown.Root onValueChange={e => handleYearChange(e)} value={String(value.getFullYear())}>
        <DropdownTrigger />
        <Dropdown.Content>
          {yearRange.map(year => (
            <Dropdown.Item value={year.toString()} key={year}>{year}</Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Root>
    </DropdownGroup>
  )
}

export default styled(DateDropdown)``
